import React, { useEffect, useState } from "react";
import { ukDayTime as fmtWhen } from "@/lib/time";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, TrendingUp, Loader2, ArrowRight, CheckCircle2, MailCheck, XCircle, Send, RefreshCw, Plug, AlarmClock, MessageSquare, KeyRound, Eraser, Gauge, PauseCircle, FileSearch, Clock3, BellRing, UploadCloud, PlayCircle, Share2, ChevronDown, ShieldCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { DeclineInsights } from "@/components/hub/DeclineInsights";
import { DealAlertsCard } from "@/components/hub/DealAlerts";
import { DocRequests } from "@/components/portal/DocRequests";
import { PendingPartnersCard, QuietSeatsCard } from "@/components/hub/SeatCards";
import { DemoLinksCard } from "@/components/hub/DemoLinksCard";
import { ClientChasesCard } from "@/components/hub/ClientChasesCard";
import { CompanyCreditsCard } from "@/components/hub/CompanyCreditsCard";
import { Card, Section, Quiet, Note, Pill, Figure, RangeToggle, PageHead, Money, gbp, LiveDot } from "@/components/hub/Shell";
import { StatStrip } from "@/components/portal/crm/kit";

/** A compact count tile for the triage strip — links to a page or scrolls to a card on this one. */
const Act = ({ n, label, tone, to, onClick, testId }) => {
  const inner = (<><div className="edge" /><div><div className="n">{n}</div><div className="t">{label}</div></div></>);
  const cls = `act${tone ? ` ${tone}` : ""}`;
  return to
    ? <Link className={cls} to={to} data-testid={testId}>{inner}</Link>
    : <button type="button" className={cls} onClick={onClick} data-testid={testId} style={{ textAlign: "left" }}>{inner}</button>;
};

const scrollToId = (id) => () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function HubOverview() {
  const [data, setData] = useState(null);
  const [book, setBook] = useState(null);
  const [triage, setTriage] = useState({ alerts: 0, stalled: 0, replies: 0, pending: 0, apps: 0, quiet: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const get = (p, f) => api.get(p).then((r) => r.data).catch(() => f);
    (async () => {
      const [s, v, al, st, w, pp, ap, qs] = await Promise.all([
        get("/hub/summary", null),
        get("/hub/pipeline/value", null),
        get("/hub/deal-alerts", { items: [] }),
        get("/hub/stalled-deals", { items: [] }),
        get("/hub/chat/unanswered", { items: [] }),
        get("/hub/partners/pending", { items: [] }),
        get("/hub/applications?status=pending", { items: [] }),
        get("/hub/seats/quiet", { items: [] }),
      ]);
      setData(s); setBook(v);
      setTriage({
        alerts: (al.items || []).filter((a) => !a.cleared_at && !a.resolved_at).length || (al.items || []).length,
        stalled: (st.items || []).length,
        replies: (w.items || []).length,
        pending: (pp.items || []).length,
        apps: ((ap.lenders || []).length + (ap.referrers || []).length) || (ap.items || []).length,
        quiet: (qs.items || []).length,
      });
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="grid place-items-center py-24 text-slate-400"><Loader2 className="size-6 animate-spin" /></div>;

  const l = data?.leads || {};
  const o = book?.totals?.open || {};
  const wm = book?.totals?.won_month || {};
  const stats = [
    { k: "Live book, net", v: gbp(o.net_gbp, { compact: true }), h: `${o.deals || 0} open · ${gbp(o.weighted_net_gbp, { compact: true })} weighted`, testId: "hub-stat-book" },
    { k: "Routed out", v: String(l.sent || 0), h: "Cases with a desk", testId: "hub-stat-routed" },
    { k: "Funded this month", v: gbp(wm.net_gbp, { compact: true }), tone: "green", h: `${wm.deals || 0} settled since the 1st`, testId: "hub-stat-funded-month" },
    { k: "Conversion", v: `${l.conversion_pct || 0}%`, h: `${l.completed || 0} of ${l.total || 0} funded`, testId: "hub-stat-conversion" },
  ];

  return (
    <div className="space-y-10 pb-6" data-testid="hub-overview">
      {/* Page head — the two things you do from here live up here, not inside a card */}
      <PageHead
        eyebrow="Connexis hub"
        title="Desk tools"
        sub="Everything waiting on a person, the live book, and the plumbing — one place to run the desk."
        right={
          <>
            <Link to="/hub/leads" className="btn btn-primary text-white" data-testid="hub-cta-new-lead">
              Enter new lead <ArrowRight className="size-3.5" />
            </Link>
            <Link to="/hub/lenders" className="btn btn-ghost" data-testid="hub-cta-add-lender">
              Add lender
            </Link>
          </>
        }
      />

      {/* Headline — the strong core, the same figures the pipeline ledger prints */}
      <StatStrip testId="hub-tools-stats" items={stats} />

      <ClientChasesCard />

      {/* Triage — the handful of things actually waiting on a person, jumping out of the page */}
      <Section title="Needs the desk now" hint="Waiting on a person, not a process. Clear these first." testId="hub-zone-triage">
        <div className="strip" data-testid="hub-triage-strip">
          <Act n={triage.alerts}  label="Deal alerts open"        tone={triage.alerts ? "amber" : undefined} onClick={scrollToId("hub-card-alerts")}  testId="hub-triage-alerts" />
          <Act n={triage.stalled} label="Desks past SLA"          tone={triage.stalled ? "red" : undefined}  onClick={scrollToId("hub-card-stalled")} testId="hub-triage-stalled" />
          <Act n={triage.replies} label="Partner replies waiting" tone={triage.replies ? "blue" : undefined} to="/hub/messages"                       testId="hub-triage-replies" />
          <Act n={triage.pending} label="Sign-ups to approve"     tone={triage.pending ? "amber" : undefined} to="/hub/referrers?pending=1"           testId="hub-triage-pending" />
          <Act n={triage.apps}    label="Applications to approve"  tone={triage.apps ? "amber" : undefined}   to="/hub/applications"                   testId="hub-triage-apps" />
          <Act n={triage.quiet}   label="Seats gone quiet"        tone={triage.quiet ? "blue" : undefined}   onClick={scrollToId("hub-card-seats")}   testId="hub-triage-quiet" />
        </div>
      </Section>

      {/* 1 — waiting on someone */}
      <Section title="On your desk" hint="Open items on cases and partners." testId="hub-zone-action">
        <div id="hub-card-alerts"><DealAlertsCard /></div>
        <div id="hub-card-stalled"><StalledDealsCard /></div>
        <div className="grid gap-4 lg:grid-cols-2">
          <PendingPartnersCard />
          <div id="hub-card-seats"><QuietSeatsCard /></div>
          <ChaseQueueCard />
          <WaitingRepliesCard />
          <OpenDocRequestsCard />
          <DeskCapacityCard />
          <CompanyCreditsCard />
        </div>
      </Section>

      {/* 2 — the numbers */}
      <Section title="Pipeline" hint="Volume, conversion, and where cases are landing." testId="hub-zone-pipeline">
        <BookValueCard />
        <div className="grid gap-4 lg:grid-cols-2">
          <Card label="Top lenders by volume" Icon={Users} testId="hub-top-lenders">
            {data?.top_lenders?.length ? (
              <div className="mt-3 divide-y divide-slate-100">
                {data.top_lenders.map((t, i) => (
                  <div key={t.lender_id} className="flex items-center gap-3 py-2.5 text-[13px]">
                    <span className="w-5 shrink-0 font-mono tabular-nums text-slate-300">{i + 1}</span>
                    <span className="min-w-0 flex-1 sm:truncate font-medium text-slate-900">{t.company_name}</span>
                    <span className="w-16 text-right font-mono tabular-nums text-slate-500">{t.count} sent</span>
                    <span className="w-14 text-right font-mono tabular-nums text-teal-600">{t.completed} won</span>
                  </div>
                ))}
              </div>
            ) : (
              <Quiet>No lender activity yet.</Quiet>
            )}
          </Card>
          <DeclineInsights path="/hub/insights/declines" days={90} />
        </div>
      </Section>

      {/* 3 — who brings the work in */}
      <Section
        title="Who's feeding you"
        hint="Introducers and lenders bringing work through the door."
        testId="hub-zone-network"
        right={
          <div className="flex items-center gap-2">
            <CountLink label="Active lenders"   value={data?.lenders?.active}   total={data?.lenders?.total}   to="/hub/lenders" />
            <CountLink label="Active introducers" value={data?.referrers?.active} total={data?.referrers?.total} to="/hub/referrers" />
          </div>
        }
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <FundingSharesCard />
          <ExplainerFilmCard />
        </div>
        <LockedOutPartnersCard />
        <DemoLinksCard />
      </Section>

      {/* 4 — plumbing, quiet until it isn't */}
      <HouseKeeping />
    </div>
  );
}

/** BookValueCard — what the live book is worth to us, net of everyone we pay. */
function BookValueCard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    const pull = async () => {
      try { const r = await api.get("/hub/pipeline/value"); if (alive) setD(r.data); }
      catch { /* silent */ }
    };
    pull();
    const t = setInterval(pull, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const o = d?.totals?.open || {};
  const w = d?.totals?.won_month || {};
  return (
    <Card
      label="What the live book is worth"
      Icon={TrendingUp}
      tone="brand"
      testId="hub-book-value"
      right={
        <div className="flex items-center gap-2.5">
          <LiveDot label="Live" />
          <Link to="/hub/pipeline" className="inline-flex h-6 items-center gap-1 rounded-full border border-slate-200 px-2.5 text-[11px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900"
                data-testid="hub-book-value-open">
            Open pipeline <ArrowRight className="size-2.5" />
          </Link>
        </div>
      }
    >
      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-4">
        <div>
          <div className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">Net if it all lands</div>
          <Money value={o.net_gbp} tone="brand" size="lg" className="mt-1.5 block font-semibold" testId="hub-book-net" />
          <div className="mt-1.5 text-[11px] text-slate-400">{o.deals || 0} open cases</div>
        </div>
        <div>
          <div className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">Weighted forecast</div>
          <Money value={o.weighted_net_gbp} size="lg" className="mt-1.5 block font-semibold" testId="hub-book-weighted" />
          <div className="mt-1.5 text-[11px] text-slate-400">by how far each case has got</div>
        </div>
        <div>
          <div className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">Funded this month</div>
          <Money value={w.net_gbp} tone="in" size="lg" className="mt-1.5 block font-semibold" testId="hub-book-month" />
          <div className="mt-1.5 text-[11px] text-slate-400">{w.deals || 0} settled since the 1st</div>
        </div>
        <div>
          <div className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">Owed out on the book</div>
          <Money value={-(o.deductions_gbp || 0)} tone="out" size="lg" className="mt-1.5 block font-semibold" testId="hub-book-owed" />
          <div className="mt-1.5 text-[11px] text-slate-400">{gbp(o.gross_gbp, { compact: true })} gross in</div>
        </div>
      </div>
      <Note>
        Priced from the fee each lender agreed when the case was sent, less the introducer's own model and any
        recruiter override. {o.unpriced_deals ? `${o.unpriced_deals} case${o.unpriced_deals === 1 ? "" : "s"} can't be priced yet — no lender on them.` : "Every open case has a lender fee attached."}
      </Note>
    </Card>
  );
}

/** CountLink — the ecosystem counts, sitting in the section rule rather than in a card. */
function CountLink({ label, value, total, to }) {
  return (
    <Link to={to}
          className="rounded-xl border border-slate-200/80 bg-white px-3 py-2 transition-colors hover:border-slate-300 hover:bg-slate-50">
      <div className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</div>
      <div className="mt-0.5 text-[15px] font-semibold tabular-nums leading-none text-slate-900">
        {value ?? 0}<span className="ml-1 font-mono text-[11px] text-slate-400">/ {total ?? 0}</span>
      </div>
    </Link>
  );
}

/** HouseKeeping — delivery, uploads, CRM and demo data. Folded away on a good day;
 *  anything actually broken still surfaces because the two health cards stay mounted. */
function HouseKeeping() {
  const [open, setOpen] = useState(false);
  return (
    <Section
      title="House-keeping"
      hint="Delivery, uploads, CRM sync and demo data."
      testId="hub-zone-system"
      right={
        <button onClick={() => setOpen((o) => !o)}
                className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border border-slate-200 bg-white px-3 text-[11.5px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900"
                data-testid="hub-system-toggle">
          {open ? "Hide" : "Show"} details
          <ChevronDown className={`size-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
      }
    >
      {open ? (
        <div className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <UploadHealthCard />
            <SmsHealthCard />
          </div>
          <CrmSyncFeedCard />
          <EmailHealthCard />
          <DeletionLogCard />
          <SampleDataCard />
        </div>
      ) : (
        <div className="space-y-4">
          <Card label="Systems" Icon={ShieldCheck} tone="good" testId="hub-system-summary">
            <Quiet>
              Uploads, texts, email senders and CRM sync are watched continuously. Anything refused or
              undelivered appears here on its own — open the details to run a test or read the recent log.
            </Quiet>
          </Card>
          {/* still mounted so a real failure can never hide behind the fold */}
          <UploadHealthCard quiet />
          <SmsHealthCard quiet />
        </div>
      )}
    </Section>
  );
}

function timeAgo(iso) {
  try { const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
}

/** OpenDocRequestsCard — every document we're waiting on, across every case. */
function OpenDocRequestsCard() {
  return (
    <Card label="Documents we're chasing" Icon={FileSearch} testId="hub-open-doc-requests">
      <div className="mt-4"><DocRequests role="admin" testPrefix="hub-docreq-inbox" /></div>
    </Card>
  );
}

/** ExplainerFilmCard — did the film on /lending-network actually get watched? */
function ExplainerFilmCard() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api.get(`/hub/video-stats?days=${days}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [days]);

  if (!data) return null;

  return (
    <Card label="Explainer film" Icon={PlayCircle} testId="hub-explainer-film"
          right={<RangeToggle options={[7, 30]} value={days} onChange={setDays} testPrefix="hub-explainer-film" />}>
      <div className="mt-4 grid grid-cols-3 gap-x-4">
        <Figure label="Plays" value={data.opens} testId="hub-explainer-plays" />
        <Figure label="To the end" value={data.completes} tone="good" />
        <Figure label="Finished it" value={`${data.watched_through_pct}%`} />
      </div>

      <Quiet>
        {data.opens === 0
          ? "Nobody has pressed play yet in this window."
          : `${data.viewers} ${data.viewers === 1 ? "person" : "people"} pressed play.`}
      </Quiet>

      {(data.by_page || []).length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="hub-explainer-pages">
          {data.by_page.map((p) => (
            <span key={p.page} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 h-6 text-[11px] text-slate-600">
              <span className="font-mono text-slate-500">{p.page || "unknown"}</span>
              <span className="tabular-nums font-semibold text-slate-800">{p.opens}</span>
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

/** FundingSharesCard — who is actually putting the explainer page in front of customers. */
function FundingSharesCard() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api.get(`/hub/funding-shares?days=${days}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [days]);

  if (!data || !(data.items || []).length) return null;

  return (
    <Card label="Who's sharing the funding page" Icon={Share2} testId="hub-funding-shares"
          right={<RangeToggle options={[7, 30, 90]} value={days} onChange={setDays} testPrefix="hub-funding-shares" />}>
      <Quiet>
        {data.sharers} {data.sharers === 1 ? "introducer" : "introducers"} put it in front of {data.people}{" "}
        {data.people === 1 ? "person" : "people"} · {data.submitted} applied
      </Quiet>

      <div className="mt-2 divide-y divide-slate-100">
        {data.items.map((r) => (
          <div key={r.id} className="flex items-center gap-3 py-2.5" data-testid={`hub-funding-share-${r.id}`}>
            <Link to="/hub/referrers" className="min-w-0 flex-1 truncate text-[13px] font-medium text-slate-900 hover:text-[#0055FF]">
              {r.name}
            </Link>
            <span className="w-16 text-right text-[11.5px] tabular-nums text-slate-500">{r.people} read</span>
            <span className="hidden w-20 text-right text-[11.5px] tabular-nums text-slate-500 sm:block">{r.started} started</span>
            <span className={`w-16 text-right text-[11.5px] font-semibold tabular-nums ${
              r.submitted ? "text-teal-600" : "text-slate-300"}`}>
              {r.submitted} applied
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** UploadHealthCard — did anything fail to reach us this week? Always visible: a green
 *  "everything landed" is the point, so nothing can fail silently. */
function UploadHealthCard({ quiet = false }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);

  useEffect(() => {
    api.get(`/hub/upload-health?days=${days}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [days]);

  if (!data) return null;
  const clean = (data.failed || 0) === 0;
  if (quiet && clean) return null;

  return (
    <Card label="Uploads" Icon={UploadCloud} tone={clean ? "good" : "act"} testId="hub-upload-health"
          right={<RangeToggle options={[7, 30]} value={days} onChange={setDays} testPrefix="hub-upload-health" />}>
      {!clean && (
        <div className="mt-3">
          <Pill tone="act" testId="hub-upload-health-failed">{data.failed} refused</Pill>
        </div>
      )}

      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-[26px] font-semibold tabular-nums text-slate-900" data-testid="hub-upload-health-total">
          {data.succeeded ?? 0}
        </span>
        <span className="text-[12.5px] text-slate-500">
          file{(data.succeeded ?? 0) === 1 ? "" : "s"} landed safely in the last {data.days} days
          {clean && (data.total ?? 0) > 0 && " — nothing was refused"}
          {clean && (data.total ?? 0) === 0 && " — nobody has sent anything yet"}
        </span>
      </div>

      {!clean && (
        <>
          <div className="mt-4 divide-y divide-slate-100" data-testid="hub-upload-health-list">
            {data.items.map((f) => (
              <div key={f.id} className="flex items-start gap-3 py-2.5" data-testid={`hub-upload-fail-${f.id}`}>
                <span className="mt-1.5 size-1.5 rounded-full bg-[#0055FF] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-medium text-slate-900 truncate">{f.filename}</span>
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 text-slate-500 px-2 h-5 text-[10.5px] whitespace-nowrap">{f.where}</span>
                  </div>
                  <div className="mt-0.5 text-[12px] text-[#0044CC]">{f.reason}</div>
                  <div className="mt-0.5 text-[11.5px] text-slate-400 truncate">
                    {f.who} · {fmtWhen(f.at)}{f.size ? ` · ${prettyBytes(f.size)}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Note>
            Whoever sent it saw that same message, so they know it didn't land. Worth a call if it's a
            client who's been waiting.
          </Note>
        </>
      )}
    </Card>
  );
}

function prettyBytes(n) {
  n = Number(n) || 0;
  return n >= 1048576 ? `${(n / 1048576).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`;
}


/** ChaseQueueCard — the requests that have gone quiet. Hidden when nothing is overdue,
 *  so the overview stays quiet on a good day. */
function ChaseQueueCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/hub/doc-requests/stale").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  useEffect(() => { load(); }, []);

  const chase = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/hub/doc-requests/${id}/chase`);
      toast.success(r.data?.sent_sms ? "Client chased by text and email" : "Client chased by email");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send that chase"); }
    finally { setBusy(null); }
  };

  const nudgeAll = async () => {
    setBusy("all");
    try {
      const r = await api.post("/hub/doc-requests/escalations/run");
      toast.success(r.data?.escalated ? `Nudged ${r.data.escalated} introducer${r.data.escalated === 1 ? "" : "s"}`
                                      : "Nothing to nudge — every request has been chased recently");
      load();
    } catch { toast.error("Could not run the nudges"); }
    finally { setBusy(null); }
  };

  if (!data) return null;
  const items = data.items || [];
  if (items.length === 0) return null;

  return (
    <Card label="Gone quiet" Icon={Clock3} tone="wait" testId="hub-chase-queue"
          right={
            <div className="flex items-center gap-2">
              <Pill tone="wait" testId="hub-chase-queue-count">{items.length} waiting</Pill>
              <Button onClick={nudgeAll} disabled={busy === "all"} size="sm" variant="outline"
                      className="h-7 rounded-[10px] text-[11.5px]" data-testid="hub-chase-queue-nudge-all">
                {busy === "all" ? <Loader2 className="size-3 animate-spin" /> : <BellRing className="size-3 mr-1" />} Nudge introducers
              </Button>
            </div>
          }>
      <div className="mt-3 divide-y divide-slate-100" data-testid="hub-chase-queue-list">
        {items.slice(0, 10).map((r) => {
          const days = Math.max(1, Math.round((r.waited_hours || 0) / 24));
          const name = r.case?.business_name || r.case?.applicant_name || "Unnamed case";
          return (
            <div key={r.id} className="flex items-start gap-3 py-2.5" data-testid={`hub-chase-row-${r.id}`}>
              <span className={`mt-1.5 size-1.5 rounded-full shrink-0 ${r.never_chased ? "bg-[#0055FF]" : "bg-[#7da9ff]"}`} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Link to={`/hub/leads/${r.lead_id}`} className="truncate text-[13px] font-medium text-slate-900 hover:text-[#0055FF]">{name}</Link>
                  {r.case?.reference && <span className="hidden font-mono text-[11px] text-slate-400 sm:inline">{r.case.reference}</span>}
                  <span className={`ml-auto shrink-0 text-[11.5px] font-medium tabular-nums ${
                    r.never_chased ? "text-[#0044CC]" : "text-[#3B63A8]"}`}>
                    {r.never_chased ? "never asked" : "silent"} · {days}d
                  </span>
                </div>
                <div className="mt-0.5 truncate text-[12px] text-slate-400">{(r.items || []).join(" · ")}</div>
              </div>
              <Button onClick={() => chase(r.id)} disabled={busy === r.id} size="sm" variant="outline"
                      className="h-7 shrink-0 rounded-[10px] text-[11.5px]" data-testid={`hub-chase-now-${r.id}`}>
                {busy === r.id ? <Loader2 className="size-3 animate-spin" /> : <Send className="size-3 mr-1" />} Chase
              </Button>
            </div>
          );
        })}
        {items.length > 10 && <div className="pt-2.5 text-[11.5px] text-slate-400">+ {items.length - 10} more</div>}
      </div>

      <Note>
        A request lands here after {Math.round((data.unchased_after_hours || 48) / 24)} day{(data.unchased_after_hours || 48) / 24 === 1 ? "" : "s"} with nobody asking the client,
        or {Math.round((data.escalate_after_hours || 72) / 24)} days of client silence. Chasing texts and emails them a private upload link.
      </Note>
    </Card>
  );
}

/** DeskCapacityCard — who's paused, who's full, at a glance. Hidden when everyone's open. */
function DeskCapacityCard() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/hub/insights/capacity").then((r) => setRows(r.data.lenders || [])).catch(() => setRows([]));
  }, []);
  if (!rows) return null;
  const notable = rows.filter((r) => r.paused || r.at_capacity || r.capped);
  if (notable.length === 0) return null;

  return (
    <Card label="Desk availability" Icon={Gauge} testId="hub-desk-capacity">
      <div className="mt-3 divide-y divide-slate-100">
        {notable.map((r) => (
          <div key={r.lender_id} className="flex items-center justify-between gap-3 py-2.5"
               data-testid={`hub-capacity-row-${r.lender_id}`}>
            <div className="flex min-w-0 items-center gap-2">
              {r.paused && <PauseCircle className="size-3.5 shrink-0 text-[#5B86D6]" />}
              <span className="sm:truncate text-[13px] font-medium text-slate-900">{r.company_name}</span>
            </div>
            <div className="shrink-0 text-[12px]">
              {r.paused ? (
                <span className="font-medium text-[#3B63A8]">paused</span>
              ) : r.at_capacity ? (
                <span className="font-mono font-medium tabular-nums text-[#3B63A8]">{r.used}/{r.cap_count} · full</span>
              ) : (
                <span className="font-mono tabular-nums text-slate-500">{r.used}/{r.cap_count} {r.cap_period}</span>
              )}
            </div>
          </div>
        ))}
      </div>
      <Note>Cases still reach a full desk — they're flagged over cap so you can decide whether to push.</Note>
    </Card>
  );
}

/** DeskPressureCard — every desk sitting on a case without a decision, and the two ways out:
 *  chase them again, or take the case off them. Covers both kinds of silence — never opened, and
 *  "in review" with nothing since, which until Oct 2026 we never chased at all. */
function StalledDealsCard() {
  const [data, setData] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [chasing, setChasing] = useState(false);
  const [busy, setBusy] = useState(null);
  const [pulling, setPulling] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => api.get("/hub/desk-pressure").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  useEffect(() => {
    load();
    api.get("/hub/pull-reasons").then((r) => setReasons(r.data.items || [])).catch(() => {});
  }, []);

  const chaseNow = async () => {
    setChasing(true);
    try {
      const r = await api.post("/hub/lead-nudges/run");
      toast.success(r.data.chased
        ? `Chased ${r.data.chased} desk${r.data.chased === 1 ? "" : "s"}`
        : "Nothing to chase — every lane has moved or been chased inside the last 2 days");
      load();
    } catch { toast.error("Chase failed — check backend logs"); }
    finally { setChasing(false); }
  };

  const chaseOne = async (d) => {
    setBusy(`${d.lead_id}-${d.lender_id}`);
    try {
      const r = await api.post(`/hub/leads/${d.lead_id}/routes/${d.lender_id}/chase`, {});
      toast.success(`${r.data.desk} chased — round ${r.data.level}. Email, text and a push into their portal.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't get that out to them"); }
    finally { setBusy(null); }
  };

  const pullOne = async (d) => {
    if (!reason) return;
    setBusy(`${d.lead_id}-${d.lender_id}`);
    try {
      const r = await api.post(`/hub/leads/${d.lead_id}/routes/${d.lender_id}/pull`, { reason });
      toast.success(`${r.data.desk} is off the case — they've been told why. ${r.data.live_lanes} desk${r.data.live_lanes === 1 ? "" : "s"} still on it.`);
      setPulling(null); setReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't go through"); }
    finally { setBusy(null); }
  };

  if (!data) return null;
  const items = data.items || [];

  return (
    <Card label="Desks sitting on a decision" Icon={AlarmClock} tone={items.length ? "wait" : "plain"}
          testId="stalled-deals-card"
          right={
            <div className="flex items-center gap-2">
              {items.length > 0 && (
                <Pill tone="wait" testId="stalled-deals-count">
                  {data.unopened} not opened · {data.in_review} in review · £{Math.round(data.amount_gbp || 0).toLocaleString()}
                </Pill>
              )}
              <Button onClick={chaseNow} disabled={chasing} size="sm" variant="outline" className="h-7 rounded-[10px] text-[11.5px]" data-testid="stalled-deals-chase">
                {chasing ? <Loader2 className="size-3 animate-spin" /> : <Send className="size-3 mr-1" />} Chase all
              </Button>
            </div>
          }>
      {items.length === 0 ? (
        <>
          <Quiet testId="stalled-deals-empty">
            Nothing sitting — every lane has either moved or been chased inside {data.threshold_days} days.
          </Quiet>
          <Note>
            A desk is chased every {data.threshold_days} days until it decides, whether it has opened the
            case or marked it in review and gone silent. From the {data.escalate_at}rd round we tell them
            the case will be taken back, and you get an alert so you can make that call.
          </Note>
        </>
      ) : (
        <div className="mt-3 divide-y divide-slate-100" data-testid="stalled-deals-list">
          {items.slice(0, 12).map((d) => {
            const key = `${d.lead_id}-${d.lender_id}`;
            const hot = d.days_waiting >= 7 || d.escalated;
            return (
              <div key={key} data-testid={`stalled-deal-${d.lead_id}`}>
                <div className="flex items-center gap-3 py-2.5 text-[13px]">
                  <span className={`size-1.5 shrink-0 rounded-full ${hot ? "bg-[#0055FF] attn-dot" : "bg-[#7da9ff]"}`} />
                  <Link to={`/hub/leads/${d.lead_id}`} className="min-w-0 flex-1 sm:truncate font-medium text-slate-900 hover:text-[#0055FF]">{d.business || d.applicant}</Link>
                  <span className="hidden w-40 truncate text-slate-500 md:block">{d.lender_name}</span>
                  <span className={`hidden w-20 shrink-0 text-[10.5px] font-semibold uppercase tracking-wider lg:block ${d.kind === "unopened" ? "text-slate-400" : "text-[#B45309]"}`}>
                    {d.kind === "unopened" ? "Unopened" : "In review"}
                  </span>
                  <span className="w-24 shrink-0 text-right font-mono text-[12px] tabular-nums text-slate-500">£{Math.round(d.amount_gbp).toLocaleString()}</span>
                  <span className={`w-14 text-right font-medium tabular-nums ${hot ? "text-[#0044CC]" : "text-[#3B63A8]"}`}>{d.days_waiting}d</span>
                  <span className="hidden w-28 truncate text-right text-[11px] text-slate-400 xl:block">
                    {!d.lender_reachable ? "no portal login"
                      : d.nudge_count ? `chased ${d.nudge_count}×` : "not chased"}
                  </span>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button size="sm" variant="outline" disabled={busy === key || !d.lender_reachable}
                            onClick={() => chaseOne(d)} className="h-6 rounded-[8px] px-2 text-[11px]"
                            data-testid={`stalled-chase-${d.lead_id}-${d.lender_id}`}>
                      {busy === key && pulling !== key ? <Loader2 className="size-3 animate-spin" /> : "Chase"}
                    </Button>
                    <Button size="sm" variant="ghost"
                            onClick={() => { setPulling(pulling === key ? null : key); setReason(reasons[0] || ""); }}
                            className="h-6 rounded-[8px] px-2 text-[11px] text-slate-500 hover:text-[#DC2626]"
                            data-testid={`stalled-pull-${d.lead_id}-${d.lender_id}`}>
                      Pull
                    </Button>
                  </div>
                </div>
                {pulling === key && (
                  <div className="flex flex-wrap items-center gap-2 pb-3 pl-[18px]" data-testid={`stalled-pull-form-${d.lead_id}-${d.lender_id}`}>
                    <select value={reason} onChange={(e) => setReason(e.target.value)}
                            className="h-7 rounded-[8px] border border-slate-200 bg-white px-2 text-[11.5px] text-slate-700"
                            data-testid="stalled-pull-reason">
                      {reasons.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                    <Button size="sm" disabled={busy === key || !reason} onClick={() => pullOne(d)}
                            className="h-7 rounded-[8px] bg-[#DC2626] px-2.5 text-[11.5px] text-white hover:bg-[#B91C1C]"
                            data-testid="stalled-pull-confirm">
                      Take it off {d.lender_name}
                    </Button>
                    <span className="text-[11px] text-slate-400">
                      They get an email, a text and a push saying why. The introducer sees it was withdrawn, not declined.
                    </span>
                  </div>
                )}
              </div>
            );
          })}
          {items.length > 12 && <div className="pt-2.5 text-[11.5px] text-slate-400">+ {items.length - 12} more</div>}
          <Note>{data.note}</Note>
        </div>
      )}
    </Card>
  );
}

/** DeletionLogCard — the receipt for every record cleared out of the Hub. */
function DeletionLogCard() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/hub/purge/log?limit=12").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  if (!items) return null;
  return (
    <Card label="Deleted records" Icon={Trash2} testId="hub-deletion-log"
          right={items.length > 0 ? <Pill testId="hub-deletion-log-count">{items.length} shown</Pill> : null}>
      {items.length === 0 ? (
        <Quiet testId="hub-deletion-log-empty">
          Nothing has been deleted. When you clear a hoax lead or a duplicate application it's logged here —
          who removed it, when, and what went with it.
        </Quiet>
      ) : (
        <div className="mt-3 divide-y divide-slate-100">
          {items.map((r) => (
            <div key={r.id} className="flex items-center gap-3 py-2.5 text-[13px]" data-testid={`hub-deletion-${r.id}`}>
              <span className="size-1.5 shrink-0 rounded-full bg-slate-300" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-slate-900">{r.describes}</div>
                <div className="truncate text-[11.5px] text-slate-400">
                  {r.kind_label} · {r.by}{r.reason ? ` · ${r.reason}` : ""}
                  {Object.keys(r.cascade || {}).length ? ` · +${Object.values(r.cascade).reduce((a, b) => a + b, 0)} attached` : ""}
                </div>
              </div>
              <span className="w-16 shrink-0 text-right text-[11px] text-slate-400">{timeAgo(r.at)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/** DeclineInsights lives in its own module; everything else on this page is below. */
function SampleDataCard() {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState({});     // `${kind}:${id}` -> true
  const [deals, setDeals] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/hub/sample-data").then((r) => {
    setData(r.data);
    const pre = {};
    (r.data.partners || []).forEach((p) => { if (p.group === "sample") pre[`${p.kind}:${p.id}`] = true; });
    setPicked(pre);
    setDeals(false);
    setConfirm(false);
  }).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  if (!data) return null;
  const partners = data.partners || [];
  const chosen = partners.filter((p) => picked[`${p.kind}:${p.id}`]);
  const nothingToDo = partners.length === 0 && !data.demo_deals;
  if (nothingToDo && !data.demo_purged) return null;

  const touchesDemo = chosen.some((p) => p.group === "demo") || (deals && data.demo_deals > 0);

  const purge = async () => {
    setBusy(true);
    try {
      const r = await api.post("/hub/sample-data/purge", {
        partners: chosen.map((p) => ({ kind: p.kind, id: p.id })),
        demo_deals: deals,
      });
      toast.success(`Removed ${r.data.partners_removed} record${r.data.partners_removed === 1 ? "" : "s"}`
        + (r.data.demo_deals_removed ? ` and ${r.data.demo_deals_removed} demo deals` : ""));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove that"); }
    finally { setBusy(false); }
  };

  const restore = async () => {
    setBusy(true);
    try {
      await api.post("/hub/sample-data/restore-demo");
      toast.success("Demo showcase data restored");
      load();
    } catch { toast.error("Could not restore the demo data"); }
    finally { setBusy(false); }
  };

  return (
    <Card label="Sample &amp; demo data" Icon={Eraser} testId="sample-data-card"
          right={(data.live_partners > 0 || data.live_deals > 0) ? (
            <span className="text-[11px] tabular-nums text-slate-400">
              {data.live_partners} live partner{data.live_partners === 1 ? "" : "s"} · {data.live_deals} real deal{data.live_deals === 1 ? "" : "s"} — never touched
            </span>
          ) : null}>

      {nothingToDo ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3" data-testid="sample-data-empty">
          <div className="text-[12.5px] text-slate-400">All clear — nothing but real data in here.</div>
          {data.demo_purged && (
            <Button onClick={restore} disabled={busy} size="sm" variant="outline" className="h-7 rounded-[10px] text-[11.5px]" data-testid="sample-data-restore">
              {busy ? <Loader2 className="size-3 animate-spin" /> : <RefreshCw className="size-3 mr-1" />} Bring the demo data back
            </Button>
          )}
        </div>
      ) : (
        <>
          <Quiet>
            Tick what you want gone. Only partners that never activated and look like test records — plus the demo
            showcase accounts — can appear here, so a live partner can never be listed.
          </Quiet>

          <div className="mt-3 space-y-1.5" data-testid="sample-data-list">
            {partners.map((p) => {
              const key = `${p.kind}:${p.id}`;
              const att = Object.entries(p.attached || {}).filter(([, v]) => v > 0)
                .map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(" · ");
              return (
                <label key={key} className="flex items-start gap-2.5 rounded-xl border border-slate-200 p-2.5 cursor-pointer hover:border-slate-300 transition-colors"
                       data-testid={`sample-data-row-${p.id}`}>
                  <input type="checkbox" checked={!!picked[key]} onChange={(e) => setPicked((s) => ({ ...s, [key]: e.target.checked }))}
                         className="mt-0.5 size-4 accent-[#0055FF]" data-testid={`sample-data-pick-${p.id}`} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[13px] font-semibold text-slate-900 sm:truncate">{p.name}</span>
                      <span className={`rounded-full border px-1.5 h-4 inline-flex items-center text-[9.5px] font-semibold uppercase tracking-wider ${
                        p.group === "demo" ? "border-indigo-200 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                        {p.group === "demo" ? "demo" : "sample"}
                      </span>
                      <span className="text-[10.5px] text-slate-400">{p.kind === "lender" ? "Lender" : "Introducer"}</span>
                    </span>
                    <span className="block text-[11.5px] text-slate-400 truncate">{p.email} · {p.reason}</span>
                    {att && <span className="block text-[11px] text-slate-400">{att} will be cleaned up too</span>}
                  </span>
                </label>
              );
            })}

            {data.demo_deals > 0 && (
              <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 p-2.5 cursor-pointer hover:border-slate-300 transition-colors"
                     data-testid="sample-data-row-deals">
                <input type="checkbox" checked={deals} onChange={(e) => setDeals(e.target.checked)}
                       className="mt-0.5 size-4 accent-[#0055FF]" data-testid="sample-data-pick-deals" />
                <span>
                  <span className="block text-[13px] font-semibold text-slate-900">{data.demo_deals} planted demo deals</span>
                  <span className="block text-[11.5px] text-slate-400">The showcase pipeline so the portals and statements had something to display</span>
                </span>
              </label>
            )}
          </div>

          {confirm ? (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50/70 p-3.5" data-testid="sample-data-confirm">
              <div className="text-[13px] text-slate-800">
                Permanently remove <b>{chosen.length}</b> record{chosen.length === 1 ? "" : "s"}
                {deals && data.demo_deals ? ` and ${data.demo_deals} demo deals` : ""}?
              </div>
              {touchesDemo && (
                <p className="mt-1 text-[11.5px] text-red-700">
                  This includes demo data, so the one-click demo logins will stop working. You can bring it back from this card later.
                </p>
              )}
              <div className="mt-2.5 flex items-center gap-2">
                <Button onClick={purge} disabled={busy} size="sm" className="h-8 rounded-[10px] text-[12px] bg-red-600 hover:bg-red-700" data-testid="sample-data-confirm-yes">
                  {busy ? <Loader2 className="size-3.5 animate-spin" /> : "Yes, remove them"}
                </Button>
                <Button onClick={() => setConfirm(false)} size="sm" variant="outline" className="h-8 rounded-[10px] text-[12px]" data-testid="sample-data-confirm-no">Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <Button onClick={() => setConfirm(true)} disabled={chosen.length === 0 && !deals} size="sm"
                      className="h-8 rounded-[10px] text-[12px] bg-[#0055FF] hover:bg-[#0044CC]" data-testid="sample-data-remove">
                <Eraser className="size-3.5 mr-1.5" /> Remove selected
                {chosen.length + (deals ? 1 : 0) > 0 ? ` (${chosen.length + (deals ? 1 : 0)})` : ""}
              </Button>
              {data.demo_purged && (
                <Button onClick={restore} disabled={busy} size="sm" variant="outline" className="h-8 rounded-[10px] text-[12px]" data-testid="sample-data-restore">
                  <RefreshCw className="size-3 mr-1" /> Bring the demo data back
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function LockedOutPartnersCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // party_id | "all"
  const [confirmAll, setConfirmAll] = useState(false);

  const load = () => api.get("/hub/partners/pending-activation").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  useEffect(() => { load(); }, []);

  const sendOne = async (row) => {
    setBusy(row.party_id);
    try {
      await api.post(`/hub/partners/${row.kind}/${row.party_id}/access-link`);
      toast.success(`Set-up link sent to ${row.email}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the link"); }
    finally { setBusy(null); }
  };

  const sendAll = async () => {
    setBusy("all");
    setConfirmAll(false);
    try {
      const r = await api.post("/hub/partners/resend-unclaimed");
      toast.success(r.data.failed
        ? `${r.data.sent} links sent · ${r.data.failed} couldn't be delivered`
        : `${r.data.sent} set-up link${r.data.sent === 1 ? "" : "s"} sent`);
      load();
    } catch { toast.error("Bulk send failed"); }
    finally { setBusy(null); }
  };

  if (!data) return null;
  const items = data.items || [];

  return (
    <Card label="Partners who haven't set a password" Icon={KeyRound}
          tone={items.length ? "wait" : "plain"} testId="locked-out-card"
          right={items.length > 0 ? (
            <div className="flex items-center gap-2">
              <Pill tone="wait" testId="locked-out-count">{items.length} waiting</Pill>
              <Button onClick={() => setConfirmAll(true)} disabled={busy === "all"} size="sm" className="h-7 rounded-[10px] bg-[#0055FF] text-[11.5px] hover:bg-[#0044CC]" data-testid="locked-out-send-all">
                {busy === "all" ? <Loader2 className="size-3 animate-spin" /> : <Send className="size-3 mr-1" />} Send set-up links to all
              </Button>
            </div>
          ) : null}>
      {confirmAll && (
        <div className="mt-3 rounded-xl border border-[#0055FF]/25 bg-[#0055FF]/[0.05] p-3.5" data-testid="locked-out-confirm">
          <div className="text-[13px] text-slate-800">
            Email <b>{items.length}</b> partner{items.length === 1 ? "" : "s"} a fresh set-up link (and text them where we have a mobile)?
          </div>
          <p className="mt-1 text-[11.5px] text-slate-500">Check the list below first — anyone with a made-up email will simply bounce.</p>
          <div className="mt-2.5 flex items-center gap-2">
            <Button onClick={sendAll} size="sm" className="h-8 rounded-[10px] text-[12px] bg-[#0055FF] hover:bg-[#0044CC]" data-testid="locked-out-confirm-yes">
              Yes, send {items.length} link{items.length === 1 ? "" : "s"}
            </Button>
            <Button onClick={() => setConfirmAll(false)} size="sm" variant="outline" className="h-8 rounded-[10px] text-[12px]" data-testid="locked-out-confirm-no">
              Cancel
            </Button>
          </div>
        </div>
      )}
      {items.length === 0 ? (
        <Quiet testId="locked-out-empty">
          Everyone's in — every lender and introducer on the books has an active password.
        </Quiet>
      ) : (
        <div className="mt-3 divide-y divide-slate-100" data-testid="locked-out-list">
          {items.slice(0, 12).map((p) => (
            <div key={p.party_id} className="flex items-center gap-3 py-2.5 text-[13px]" data-testid={`locked-out-${p.party_id}`}>
              <span className={`size-1.5 shrink-0 rounded-full ${p.invite_expired ? "bg-[#0055FF]" : "bg-[#7da9ff]"}`} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-slate-900">{p.name}</div>
                <div className="truncate text-[11.5px] text-slate-400">{p.email} · {p.kind === "lender" ? "Lender" : "Introducer"}</div>
              </div>
              <span className="hidden w-40 text-right text-[11px] text-slate-400 sm:block">
                {p.access_link_sent_at ? `link sent ${timeAgo(p.access_link_sent_at)}`
                  : p.invite_expired ? "invite expired" : "invite not opened"}
              </span>
              <Button onClick={() => sendOne(p)} disabled={busy === p.party_id} size="sm" variant="outline"
                      className="h-7 shrink-0 rounded-[10px] text-[11px]" data-testid={`locked-out-send-${p.party_id}`}>
                {busy === p.party_id ? <Loader2 className="size-3 animate-spin" /> : "Send link"}
              </Button>
            </div>
          ))}
          {items.length > 12 && <div className="pt-2.5 text-[11.5px] text-slate-400">+ {items.length - 12} more — use "Send set-up links to all"</div>}
        </div>
      )}
    </Card>
  );
}

function waitLabel(m) {
  if (m < 60) return `${m}m`;
  if (m < 2160) return `${Math.round(m / 60)}h`;
  return `${Math.round(m / 1440)}d`;
}

function WaitingRepliesCard() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  const load = () => api.get("/hub/chat/unanswered").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  useEffect(() => { load(); }, []);

  const alertNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/hub/chat/alerts/run");
      toast.success(r.data.alerted ? `Alerted you about ${r.data.alerted} waiting conversation${r.data.alerted === 1 ? "" : "s"}`
                                   : "Nothing overdue — every message is inside the reply window or already alerted");
      load();
    } catch { toast.error("Could not run the check"); }
    finally { setRunning(false); }
  };

  if (!data) return null;
  const items = (data.items || []).filter((i) => !i.too_old);
  const overdue = items.filter((i) => i.overdue);

  return (
    <Card label="Partners waiting on a reply" Icon={MessageSquare}
          tone={overdue.length ? "act" : "plain"} testId="waiting-replies-card"
          right={
            <div className="flex items-center gap-2">
              {overdue.length > 0 && (
                <Pill tone="act" testId="waiting-replies-count">{overdue.length} past {waitLabel(data.threshold_minutes || 60)}</Pill>
              )}
              {!data.alerts_enabled && <Pill testId="waiting-replies-off">Alerts off</Pill>}
              <Button onClick={alertNow} disabled={running} size="sm" variant="outline" className="h-7 rounded-[10px] text-[11.5px]" data-testid="waiting-replies-run">
                {running ? <Loader2 className="size-3 animate-spin" /> : <Send className="size-3 mr-1" />} Check now
              </Button>
            </div>
          }>
      {items.length === 0 ? (
        <>
          <Quiet testId="waiting-replies-empty">Inbox clear — every partner message has been answered.</Quiet>
          <Note>
            If one sits unanswered for {waitLabel(data.threshold_minutes || 60)} you'll get an email and a text,
            repeated every {data.realert_hours || 4}h up to {data.max_alerts || 3} times.
          </Note>
        </>
      ) : (
        <div className="mt-3 divide-y divide-slate-100" data-testid="waiting-replies-list">
          {items.slice(0, 10).map((t) => (
            <div key={t.thread_id} className="flex items-center gap-3 py-2.5 text-[13px]" data-testid={`waiting-reply-${t.thread_id}`}>
              <span className={`size-1.5 shrink-0 rounded-full ${t.overdue ? "bg-[#0055FF]" : "bg-[#7da9ff]"}`} />
              <Link to="/hub/messages" className="w-40 shrink-0 truncate font-medium text-slate-900 hover:text-[#0055FF]">{t.party_name}</Link>
              <span className="hidden min-w-0 flex-1 truncate text-slate-400 md:block">“{(t.last_body || "").slice(0, 60)}”</span>
              <span className={`ml-auto w-16 text-right font-medium tabular-nums ${t.overdue ? "text-[#0044CC]" : "text-[#3B63A8]"}`}>
                {waitLabel(t.waiting_minutes)}
              </span>
              <span className="hidden w-36 truncate text-right text-[11px] text-slate-400 sm:block">
                {t.alerts_exhausted ? `${t.alert_count}/${data.max_alerts} alerts — capped`
                  : t.alerted_at ? `alerted ${timeAgo(t.alerted_at)}`
                  : t.overdue ? "not alerted yet" : "inside the window"}
              </span>
            </div>
          ))}
          {items.length > 10 && <div className="pt-2.5 text-[11.5px] text-slate-400">+ {items.length - 10} more</div>}
        </div>
      )}
    </Card>
  );
}

function SmsHealthCard({ quiet = false }) {
  const [info, setInfo] = useState(null);
  const [to, setTo] = useState("");
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/hub/sms-health").then((r) => setInfo(r.data)).catch(() => setInfo(null));
  useEffect(() => { load(); }, []);
  if (!info) return null;

  const test = async () => {
    if (!to.trim()) return;
    setTesting(true);
    try {
      const r = await api.post("/hub/sms-health/test", { to: to.trim() });
      if (r.data.ok) toast.success(`Delivered to ${r.data.to} (${r.data.status})`);
      else toast.error(`Not delivered to ${r.data.to || to} — ${r.data.status}${r.data.detail ? `: ${r.data.detail}` : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  const t = info.totals || {};
  const cov = info.coverage || {};
  const healthy = info.configured && !(t.invalid || 0);
  if (quiet && healthy) return null;

  return (
    <Card label="SMS delivery" Icon={MessageSquare} tone={info.configured ? "plain" : "act"} testId="sms-health-card"
          right={
            <Pill tone={info.configured ? "green" : "red"}>
              {info.configured ? `ClickSend live · sender ${info.sender_id || "default"}` : "ClickSend not configured"}
            </Pill>
          }>
      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-4">
        <Figure label="Sent" value={t.sent || 0} />
        <Figure label="Delivered" value={t.delivered || 0} tone="good" />
        <Figure label="Rejected" value={t.invalid || 0} tone={t.invalid ? "wait" : "slate"} />
        <Figure label="Mobiles on file" value={`${(cov.lenders || 0) + (cov.introducers || 0)}/${(cov.lenders_total || 0) + (cov.introducers_total || 0)}`} />
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="07… — send one real test text"
               className="h-9 max-w-[240px]" data-testid="sms-health-to" />
        <Button onClick={test} disabled={testing || !to.trim()} size="sm" variant="outline" className="h-9 rounded-[10px] text-[12px]" data-testid="sms-health-test">
          {testing ? <Loader2 className="size-3.5 animate-spin" /> : <><Send className="size-3 mr-1" /> Test</>}
        </Button>
      </div>

      {info.recent?.length > 0 && (
        <div className="mt-4 divide-y divide-slate-100" data-testid="sms-health-recent">
          {info.recent.slice(0, 6).map((s, i) => (
            <div key={i} className="flex items-center gap-2.5 py-2 text-[12px]">
              <span className={`size-1.5 shrink-0 rounded-full ${s.ok ? "bg-teal-500" : "bg-[#7da9ff]"}`} />
              <span className="font-mono text-slate-600">{s.to}</span>
              <span className="hidden min-w-0 flex-1 truncate text-slate-400 sm:block">{s.preview}</span>
              <span className={`ml-auto whitespace-nowrap font-medium ${s.ok ? "text-teal-600" : "text-[#3B63A8]"}`}>{s.status}</span>
            </div>
          ))}
        </div>
      )}
      <Note>Texts fire on: {(info.events || []).join(" · ")}</Note>
    </Card>
  );
}

function CrmSyncFeedCard() {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = () => api.get("/hub/crm-sync/events").then((r) => setData(r.data)).catch(() => setData({ events: [] }));
  useEffect(() => { load(); }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/hub/crm-sync/run");
      toast.success(`Sync complete — ${r.data.checked} deals checked, ${r.data.updated} updated`);
      load();
    } catch { toast.error("Sync failed — check backend logs"); }
    finally { setSyncing(false); }
  };

  if (!data) return null;
  const events = data.events || [];

  return (
    <Card label="CRM sync activity" Icon={RefreshCw} testId="crm-sync-feed"
          right={
            <div className="flex items-center gap-2">
              <Pill testId="crm-sync-connected-count">
                <Plug className="size-2.5" /> {data.connected_lenders || 0} connected · {data.syncable_lenders || 0} syncable
              </Pill>
              <Button onClick={syncNow} disabled={syncing} size="sm" variant="outline" className="h-7 rounded-[10px] text-[11.5px]" data-testid="crm-sync-now">
                {syncing ? <Loader2 className="size-3 animate-spin" /> : <RefreshCw className="size-3 mr-1" />} Sync now
              </Button>
            </div>
          }>
      {events.length === 0 ? (
        <Quiet testId="crm-sync-empty">
          No CRM-synced changes yet — when a lender marks a deal won or lost in HubSpot or Pipedrive it appears
          here and the pipeline updates itself. Auto-sync runs every 30 minutes.
        </Quiet>
      ) : (
        <div className="mt-3 divide-y divide-slate-100" data-testid="crm-sync-events">
          {events.slice(0, 10).map((e) => (
            <div key={e.id} className="flex items-center gap-3 py-2.5 text-[13px]" data-testid={`crm-sync-event-${e.id}`}>
              <span className={`size-1.5 shrink-0 rounded-full ${e.to_status === "completed" ? "bg-teal-500" : "bg-[#0B1220]"}`} />
              <span className="w-40 shrink-0 truncate font-medium text-slate-900">{e.lender_name || "Lender"}</span>
              <span className="hidden min-w-0 flex-1 truncate text-slate-400 md:block">{e.lead_label}</span>
              <span className={`w-32 text-right text-[11.5px] font-medium ${e.to_status === "completed" ? "text-teal-700" : "text-[#0B1220]"}`}>
                {e.from_status || "sent"} → {e.to_status}
              </span>
              <span className="hidden w-32 truncate text-right text-[11px] capitalize text-slate-400 sm:block">{e.provider}{e.crm_stage_label ? ` · ${e.crm_stage_label}` : ""}</span>
              <span className="w-16 text-right text-[11px] text-slate-400">{timeAgo(e.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function EmailHealthCard() {
  const [info, setInfo] = useState(null);
  const [to, setTo] = useState("admin@connex.is");
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    api.get("/hub/email-health").then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, []);

  const run = async () => {
    if (!to.trim()) return;
    setRunning(true);
    setReport(null);
    try {
      const r = await api.post("/hub/email-health/test", { to: to.trim() });
      setReport(r.data);
      r.data.ok ? toast.success("All senders delivered — check your inbox") : toast.error("Some senders failed — see details below");
    } catch (e) { toast.error(e?.response?.data?.detail || "Health check failed"); }
    finally { setRunning(false); }
  };

  if (!info) return null;
  const resultFor = (kind) => report?.results?.find((x) => x.kind === kind);

  return (
    <Card label="Email delivery health" Icon={MailCheck} testId="email-health-card"
          right={
            <div className="flex items-center gap-2">
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Send test to…" className="h-9 w-56" data-testid="email-health-to" />
              <Button onClick={run} disabled={running || !to.trim()} className="h-9 rounded-[10px] bg-[#0055FF] hover:bg-[#0044CC]" data-testid="email-health-run">
                {running ? <Loader2 className="size-4 animate-spin" /> : <><Send className="size-3.5 mr-1" /> Run test</>}
              </Button>
            </div>
          }>
      <Quiet>
        Test-fires every sender so silent delivery failures can never hide. {!info.resend_configured && <b className="text-[#0044CC]">Resend API key missing.</b>}
      </Quiet>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {info.senders.map((s) => {
          const res = resultFor(s.kind);
          return (
            <div key={s.kind} className={`min-w-0 rounded-xl border p-3 ${res ? (res.sent ? "border-teal-200 bg-teal-50/50" : "border-[#0055FF]/30 bg-[#0055FF]/[0.05]") : "border-slate-200 bg-slate-50/50"}`} data-testid={`email-health-result-${s.kind}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-[9.5px] font-semibold uppercase tracking-[0.14em] text-slate-500">{s.kind}</span>
                {res && (res.sent ? <CheckCircle2 className="size-4 shrink-0 text-teal-600" /> : <XCircle className="size-4 shrink-0 text-[#0055FF]" />)}
              </div>
              <div className="mt-1.5 truncate font-mono text-[11px] text-slate-600" title={s.sender}>{s.sender || "—"}</div>
              <div className="mt-1 text-[10.5px] leading-snug text-slate-400">{s.used_for}</div>
              {res && (
                <div className={`mt-1.5 text-[10.5px] font-medium ${res.sent ? "text-teal-700" : "text-[#0044CC]"}`}>
                  {res.sent ? `Delivered · ${(res.id || "").slice(0, 8)}` : res.error}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
